import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HeightIcon from '@mui/icons-material/Height';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const DeliveryCard = ({ delivery, index }) => {
  const theme = useTheme();
  const { metrics, pitch_impact, delivery_type } = delivery;

  const getSpeedColor = (speed) => {
    if (speed > 140) return theme.palette.error.main;
    if (speed > 130) return theme.palette.warning.main;
    if (speed > 115) return theme.palette.success.main;
    return theme.palette.info.main;
  };

  const getSwingIconColor = (swingType) => {
    switch (swingType) {
      case 'inswing': return theme.palette.info.main;
      case 'outswing': return theme.palette.warning.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getLengthColor = (length) => {
    switch (length) {
      case 'yorker': return theme.palette.error.main;
      case 'full': return theme.palette.warning.main;
      case 'good': return theme.palette.success.main;
      case 'short': return theme.palette.info.main;
      default: return theme.palette.text.secondary;
    }
  };

  const formatSwingType = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Card
      className="glass"
      sx={{
        mb: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 40px ${theme.palette.primary.main}20`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Delivery #{index + 1}
          </Typography>
          <Chip 
            label={delivery_type.toUpperCase()} 
            color="primary"
            sx={{ 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          />
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {/* Speed Metrics */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="primary" />
            Speed Metrics
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Ball Speed
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: getSpeedColor(metrics.speed_kmh),
                    fontWeight: 700,
                    mb: 0.5,
                  }}
                >
                  {metrics.speed_kmh} km/h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metrics.speed_mph} mph
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Tooltip title="Speed when ball leaves bowler's hand">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Release Speed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {metrics.release_speed_kmh} km/h
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Tooltip title="Speed when ball hits the pitch">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Impact Speed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {metrics.impact_speed_kmh} km/h
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Swing & Spin */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChartIcon color="primary" />
            Swing & Spin Analysis
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, background: 'rgba(0, 0, 0, 0.2)' }}>
                <ShowChartIcon sx={{ fontSize: 32, color: getSwingIconColor(metrics.swing_type) }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Swing Type
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatSwingType(metrics.swing_type)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.abs(metrics.swing_angle_degrees).toFixed(1)}° swing
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {metrics.spin_rpm && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, background: 'rgba(0, 0, 0, 0.2)' }}>
                  <RotateRightIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Spin Rate
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {metrics.spin_rpm} RPM
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Estimated rotation
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Pitch Impact */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon color="primary" />
            Pitch Impact Analysis
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Tooltip title="Distance from bowling crease">
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTimeIcon fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      Length
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {pitch_impact.x.toFixed(2)} m
                  </Typography>
                  <Chip 
                    label={pitch_impact.length_category} 
                    size="small"
                    sx={{ 
                      backgroundColor: getLengthColor(pitch_impact.length_category),
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                </Card>
              </Tooltip>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Tooltip title="Lateral position from center">
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOnIcon fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      Line
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {pitch_impact.y.toFixed(2)} m
                  </Typography>
                  <Chip 
                    label={pitch_impact.line_category} 
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Card>
              </Tooltip>
            </Grid>
            
            {pitch_impact.bounce_height && (
              <Grid item xs={12} md={4}>
                <Tooltip title="Height after bounce">
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <HeightIcon fontSize="small" color="primary" />
                      <Typography variant="caption" color="text.secondary">
                        Bounce Height
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {pitch_impact.bounce_height.toFixed(2)} m
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      After impact
                    </Typography>
                  </Card>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeliveryCard;